//Email

import EyeIcon from "@heroicons/react/24/solid/EyeIcon";
import {
  Button,
  Divider,
  Grid,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import "ckeditor5/ckeditor5.css";
import { Fragment, useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { cleanEmailHtml } from "shared/helpers";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import useTranslation from "shared/helpers/hooks/useTranslation";
import { requiredRules } from "shared/helpers/validators";
import BasicAutocomplete from "shared/ui/Autocomplete";
import ButtonLoader from "shared/ui/ButtonLoader";
import SharedDialog from "shared/ui/Dialog";
import TextInput from "shared/ui/TextInput";
import { EDistributionMethods } from "store/enums/campaignDetails";
import { ERequestStatus } from "store/enums/index.enum";
import {
  DistributionSchedule,
  EmailDistributionImageUpload,
  GetCampaignById,
  selectCampaignInfo,
} from "store/slicers/campaignDetail";
import { GetCampaignTranslationGrid } from "store/slicers/campaignTranslations";
import {
  selectButtonLoadingState,
  setButtonLoading,
} from "store/slicers/common";
import {
  GetTriggerSources,
  selectTriggerSourceData,
} from "store/slicers/triggerSource";
import MyEditorComponent from "./HtmlEditor";
import TestEmail from "./TestEmail";
import { EBaseUrl } from "index";
import InputField from "shared/ui/TextInput";

const defaultValues = {
  ignoreQuarantine: false,
  message: "",
  numberOfTransaction: "",
  postpone: false,
  postponeTime: "",
  link: "",
  quarantinePeriod: "",
  surveyQuarantine: false,
  triggerIDs: [],
  title: "",
  name: "",
  expiation: {
    days: "",
    hours: "",
    minutes: "",
  },
  activeHours: { from: new Date(), to: new Date() },
  emailBody: "",
  emailSubject: "",
  contacts: "",
  base64: "",
};

const AutomatedForm = () => {
  const [writeLinkTextModalOpen, setWriteLinkTextModalOpen] = useState(false);
  const [isPreviewModalOpen, setPreviewModalOpen] = useState(false);
  const [isSendEmailOpen, setSendEmailOpen] = useState(false);
  const t = useTranslation();
  const { surveyID } = useParams();
  const campaignInfo = useSelector(selectCampaignInfo);
  const dispatch = useAsyncDispatch();
  const [emailBody, setEmailBody] = useState("");
  const isButtonLoading = useSelector(selectButtonLoadingState);

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const triggers = useSelector(selectTriggerSourceData);

  const editorRef = useRef(null);

  const openSendTestEmail = () => {
    setSendEmailOpen(true);
  };

  const onSubmit = async (formData) => {
    let editorContent = editorRef?.current?.getContent();
    const base64ImgTag = editorContent.match(
      /<img[^>]+src="data:image\/[^">]+"/
    );
    if (base64ImgTag && formData.base64) {
      const rawBase64 = formData.base64.split(",")[1];
      const match = formData.base64?.match(
        /^data:image\/([a-zA-Z0-9+]+);base64,/
      );
      const extension = match ? match[1] : "";
      const { meta, payload } = await dispatch(
        EmailDistributionImageUpload({
          base64Image: rawBase64,
          extension: `.${extension}`,
          removeImage: false,
        })
      );

      if (meta.requestStatus !== ERequestStatus.FULFILLED) {
        dispatch(setButtonLoading(false));
        return;
      }

      const base64 = base64ImgTag[0].match(/src="([^"]+)"/)?.[1];
      editorContent = editorContent.replace(
        base64,
        `${EBaseUrl.MediaImagesUrl}/${payload}`
      );
    }

    if (!editorContent) {
      toast.error(t("fill_email_body"));
      return;
    }
    dispatch(setButtonLoading(true));
    const { triggerIDs, numberOfTransaction, ...rest } = formData;
    const htmlContent = cleanEmailHtml(editorContent);
    const data = {
      ...rest,
      ...(Number(numberOfTransaction)
        ? { numberOfTransaction: Number(numberOfTransaction) }
        : {}),
      triggerIDs: triggerIDs.map((item) => {
        return item.id;
      }),
      distributionMethod: EDistributionMethods.Email,
      isLink: true,
      emailBody: htmlContent,
    };
    const { meta } = await dispatch(
      DistributionSchedule({ id: Number(surveyID), data })
    );

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      dispatch(setButtonLoading(false));
      return;
    }

    toast.success("Campaign schedule is set.");
    dispatch(setButtonLoading(false));
  };

  const openPreviewModal = () => {
    if (!editorRef.current.getContent()) {
      toast.error(t("fill_email_body"));
      return;
    }
    setEmailBody(editorRef.current.getContent());
    setPreviewModalOpen(true);
  };

  const insertLink = () => {
    if (editorRef.current) {
      const linkValue = methods.getValues("link");

      const editor = editorRef.current;

      // Remove existing links first
      editor.dom.remove(editor.dom.select('a[href="{link}"]'));

      // Insert new link
      editor.insertContent(`<a href="{link}" target="_blank">${linkValue}</a>`);
    }
  };

  useEffect(() => {
    const distributionInfo = campaignInfo?.campaignTriggers.find(
      (c) => c.distributionMethod === EDistributionMethods.Email
    );
    const selectedTriggerIds = campaignInfo?.campaignTriggers
      .filter((i) => i.distributionMethod === EDistributionMethods.Email)
      .map((t) => t.TriggerId);
    if (distributionInfo) {
      const default_language_data = campaignInfo.campaignTranslations?.find(
        (i) => i.languageID === campaignInfo.defaultLanguageID
      );

      if (editorRef.current) {
        editorRef.current.setContent(default_language_data?.emailBody || "");
      }

      methods.reset({
        name: distributionInfo.name,
        title: default_language_data?.pushTitle,
        message: default_language_data?.pushContent,
        ignoreQuarantine: distributionInfo.ignoreQuarantine,
        numberOfTransaction: distributionInfo?.numberOfTransaction
          ? distributionInfo?.numberOfTransaction?.toString()
          : "",
        postpone: distributionInfo.postpone,
        postponeTime: distributionInfo?.postponeTime
          ? distributionInfo.postponeTime.toString()
          : "",
        quarantinePeriod: distributionInfo?.quarantinePeriod
          ? distributionInfo.quarantinePeriod.toString()
          : "",
        surveyQuarantine: distributionInfo.surveyQuarantine,
        triggerIDs: triggers.filter((i) => selectedTriggerIds.includes(i.id)),
        emailBody: default_language_data?.emailBody,
        emailSubject: default_language_data?.emailSubject,
      });
    }
  }, [campaignInfo, methods, triggers]);

  useEffect(() => {
    if (!surveyID) {
      return;
    }
    Promise.all([
      dispatch(GetTriggerSources()),
      dispatch(GetCampaignById(Number(surveyID))),
      dispatch(GetCampaignTranslationGrid(surveyID)),
    ]);
  }, [dispatch, surveyID]);

  return (
    <Box p={4} m={2} component={Paper}>
      <FormProvider {...methods}>
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12} md={6}>
            <Typography fontSize={18} mb={"30px"}>
              {t("schedule")}
            </Typography>
            <Box display="flex" alignItems={"stretch"} mb={2} gap={2}>
              <Box display="flex" alignItems="center" flex={1}>
                <Tooltip
                  title={
                    "Here you can select the trigger source from where you want to receive the triggers. "
                  }
                >
                  <Typography>{t("trigger_source")}</Typography>
                </Tooltip>
              </Box>
              <Box flex={1}>
                <BasicAutocomplete
                  options={triggers}
                  inputLabel={"triggers"}
                  name={"triggerIDs"}
                  onChangeCB={() => methods.trigger("triggerIDs")}
                  rules={requiredRules}
                  optionLabel="name"
                  defaultValue={[]}
                  multiple
                />
              </Box>
            </Box>
            <Box display="flex" alignItems={"stretch"} gap={2} mb={2}>
              <Box display="flex" alignItems="center" flex={1}>
                <Tooltip
                  title={
                    "In case activating this function the survey will be sent to the customer after the predefined minutes.  "
                  }
                >
                  <Typography>{t("postpone_survey")}</Typography>
                </Tooltip>
              </Box>
              <Box flex={1}>
                <Controller
                  name="postpone"
                  control={methods.control}
                  render={({ field }) => (
                    <Switch color="success" {...field} checked={field.value} />
                  )}
                />
              </Box>
            </Box>
            {methods.watch("postpone") && (
              <Box display="flex" alignItems={"stretch"} mb={2}>
                <Box display="flex" alignItems="center" flex={1}>
                  <Typography>{t("postpone_survey_for")}</Typography>
                </Box>
                <Box flex={1}>
                  <TextInput
                    rules={requiredRules}
                    label="minutes"
                    name="postponeTime"
                  />
                </Box>
              </Box>
            )}
            <Divider />
            <Typography mt={2} fontSize={18} mb={2}>
              {t("quarantine")}
            </Typography>
            <Box display="flex" justifyContent={"space-between"}>
              <Box sx={{ width: "100%" }}>
                <Box display="flex" alignItems={"stretch"} mb={2} gap={2}>
                  <Box display="flex" alignItems="center" flex={1}>
                    <Tooltip
                      title={
                        "In case activating this function the customers will be quarantined after the predefined transactions."
                      }
                    >
                      <Typography>{t("customer_quarantine")}</Typography>
                    </Tooltip>
                  </Box>
                  <Box flex={1}>
                    <Controller
                      name="surveyQuarantine"
                      control={methods.control}
                      render={({ field }) => (
                        <Switch
                          color="success"
                          {...field}
                          checked={field.value}
                        />
                      )}
                    />
                  </Box>
                </Box>
                {methods.watch("surveyQuarantine") && (
                  <Fragment>
                    <Box display="flex" alignItems={"stretch"} mb={2}>
                      <Box display="flex" alignItems="center" flex={1}>
                        <Typography>{t("quarantine_the_customer")}</Typography>
                      </Box>
                      <Box flex={1}>
                        <TextInput
                          rules={requiredRules}
                          label="transaction"
                          onlyNumbers
                          name="numberOfTransaction"
                        />
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      justifyContent={"space-between"}
                      alignItems={"stretch"}
                      mb={2}
                    >
                      <Box display="flex" alignItems="center" flex={1}>
                        <Typography>
                          {t("quarantine_the_customer_for")}
                        </Typography>
                      </Box>
                      <Box flex={1}>
                        <TextInput
                          rules={requiredRules}
                          label="days"
                          name="quarantinePeriod"
                        />
                      </Box>
                    </Box>
                  </Fragment>
                )}
                <Box
                  display="flex"
                  justifyContent={"space-between"}
                  alignItems={"stretch"}
                  mb={2}
                  gap={2}
                  pb={8}
                >
                  <Box display="flex" alignItems="center" flex={1}>
                    <Tooltip
                      title={
                        "In case activation of this function the survey will be sent the quarantined customers as well after receiving the trigger."
                      }
                    >
                      <Typography>
                        {t("include_the_quarantined_customers")}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box flex={1}>
                    <Controller
                      name="ignoreQuarantine"
                      control={methods.control}
                      render={({ field }) => (
                        <Switch
                          color="success"
                          {...field}
                          checked={field.value}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Divider />
              </Box>
              <Box />
            </Box>
          </Grid>
          <Grid item xs={12} md={6} sx={{ pb: { xs: 10, md: 0 } }}>
            <Box
              mb={2}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography fontSize={18}>{t("content")}</Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  onClick={openSendTestEmail}
                  variant="outlined"
                  startIcon={<EyeIcon height={20} />}
                >
                  <Typography>{t("test_email")}</Typography>
                </Button>
                <Button
                  onClick={openPreviewModal}
                  variant="outlined"
                  startIcon={<EyeIcon height={20} />}
                >
                  <Typography>{t("preview")}</Typography>
                </Button>
              </Box>
            </Box>
            <Box mb={2}>
              <TextInput
                name="emailSubject"
                label="Subject"
                rules={requiredRules}
              />
            </Box>
            <Box sx={{ mb: 5 }}>
              <MyEditorComponent editorRef={editorRef} />
              <ButtonLoader
                onClick={() => {
                  setWriteLinkTextModalOpen(true);
                }}
                isLoading={false}
              >
                <Typography>{t("add_link")}</Typography>
              </ButtonLoader>
            </Box>
          </Grid>
        </Grid>

        <Box
          mt={2}
          width="100%"
          p={2}
          sx={{
            zIndex: 1100,
            position: "fixed",
            bottom: 0,
            right: 0,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "white",
          }}
          display="flex"
          justifyContent={"flex-end"}
        >
          <Box>
            <ButtonLoader
              fullWidth
              onClick={methods.handleSubmit(onSubmit)}
              isLoading={isButtonLoading}
              disabled={!Object.keys(methods.formState.dirtyFields)?.length}
              type="submit"
            >
              <Typography>{t("save")}</Typography>
            </ButtonLoader>
          </Box>
        </Box>
        <SharedDialog
          hasActions={false}
          maxWidth="md"
          minWidth="600px"
          open={isPreviewModalOpen}
          setOpen={setPreviewModalOpen}
          textConfig={{ title: "Preview" }}
          sx={{
            "& .MuiDialog-container": {
              "& .MuiDialogContent-root": {
                padding: 0,
              },
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" mb={3}>
              {t("subject")} : {methods.watch("emailSubject")}
            </Typography>
            <div dangerouslySetInnerHTML={{ __html: emailBody }} />
          </Box>
        </SharedDialog>
        <SharedDialog
          maxWidth="md"
          minWidth="600px"
          onSuccess={insertLink}
          open={writeLinkTextModalOpen}
          setOpen={setWriteLinkTextModalOpen}
          textConfig={{ title: "Insert link text" }}
        >
          <InputField name="link" label="link" />
        </SharedDialog>
      </FormProvider>
      <TestEmail
        isSendEmailOpen={isSendEmailOpen}
        setSendEmailOpen={setSendEmailOpen}
        surveyID={surveyID}
        t={t}
      />
    </Box>
  );
};

export default AutomatedForm;
